import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  AlertTriangle,
  Loader2,
  User,
  Phone,
  Mail,
  Calendar,
  TrendingUp,
  ClipboardList,
  FileText,
  Plus,
  Edit,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const riskColors = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-orange-100 text-orange-700 border-orange-200',
  low: 'bg-green-100 text-green-700 border-green-200',
  none: 'bg-slate-100 text-slate-700 border-slate-200',
};

const riskNames = {
  high: 'Yüksek Risk',
  medium: 'Orta Risk',
  low: 'Düşük Risk',
  none: 'Normal',
};

const assessmentTypeNames = {
  screening: 'Tarama',
  fluency: 'Akıcılık',
  decoding: 'Çözümleme',
  vocabulary: 'Kelime',
  comprehension: 'Anlama',
};

const interventionStatusNames = {
  planned: 'Planlandı',
  active: 'Aktif',
  completed: 'Tamamlandı',
  paused: 'Duraklatıldı',
  cancelled: 'İptal Edildi',
};

export const StudentDetailPage = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/api/students/${studentId}/profile`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else if (response.status === 404) {
        toast.error('Öğrenci bulunamadı');
        navigate('/students');
      } else {
        toast.error('Öğrenci bilgileri yüklenemedi');
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [studentId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const { student, assessments, interventions, sessions, notes } = profile;

  return (
    <div className="space-y-6" data-testid="student-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/students')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-heading font-bold text-primary-900">
            {student.full_name}
          </h1>
          <p className="text-muted-foreground">
            {student.class_name} • {student.grade_level}. Sınıf
          </p>
        </div>
        <Badge variant="outline" className={`text-sm ${riskColors[student.risk_level]}`}>
          {student.risk_level === 'high' && <AlertTriangle className="mr-1 h-4 w-4" />}
          {riskNames[student.risk_level]}
        </Badge>
      </div>

      {/* Student Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Öğrenci Bilgileri</p>
                <p className="font-medium">{student.full_name}</p>
                <p className="text-sm text-muted-foreground">
                  {student.gender === 'K' ? 'Kız' : 'Erkek'} • {student.birth_date || '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-secondary/10 flex items-center justify-center">
                <Phone className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Veli Bilgileri</p>
                <p className="font-medium">{student.guardian_name || '-'}</p>
                <p className="text-sm text-muted-foreground">{student.guardian_phone || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <ClipboardList className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Müdahale Durumu</p>
                <p className="font-medium">
                  {student.intervention_status ? 'Aktif Müdahale Var' : 'Müdahale Yok'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {interventions.length} plan
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Risk Flags */}
      {student.risk_flags && student.risk_flags.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <h3 className="font-semibold text-red-900">Risk Bayrakları</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {student.risk_flags.map((flag, idx) => (
                <Badge key={idx} variant="destructive" className="bg-red-600">
                  {flag.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Genel Bakış</TabsTrigger>
          <TabsTrigger value="assessments">Değerlendirmeler</TabsTrigger>
          <TabsTrigger value="interventions">Müdahaleler</TabsTrigger>
          <TabsTrigger value="notes">Notlar</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Recent Assessments Summary */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Son Değerlendirmeler</CardTitle>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/students/${studentId}/assessment/new`}>
                  <Plus className="mr-2 h-4 w-4" />
                  Yeni Değerlendirme
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {assessments.length > 0 ? (
                <div className="space-y-4">
                  {assessments.slice(0, 3).map((assessment) => (
                    <div key={assessment.assessment_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{assessmentTypeNames[assessment.assessment_type]}</p>
                        <p className="text-sm text-muted-foreground">{assessment.assessment_date}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{assessment.percentile || '-'}%</p>
                        <Badge variant="outline" className={riskColors[assessment.risk_level_result]}>
                          {riskNames[assessment.risk_level_result]}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  Henüz değerlendirme yapılmamış
                </p>
              )}
            </CardContent>
          </Card>

          {/* Active Interventions */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Aktif Müdahale Planları</CardTitle>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/students/${studentId}/intervention/new`}>
                  <Plus className="mr-2 h-4 w-4" />
                  Yeni Plan
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {interventions.length > 0 ? (
                <div className="space-y-4">
                  {interventions.map((plan) => (
                    <div key={plan.plan_id} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium">{plan.title}</h4>
                          <p className="text-sm text-muted-foreground">{plan.description}</p>
                        </div>
                        <Badge variant="outline">
                          {interventionStatusNames[plan.status]}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {plan.target_areas?.map((area, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {area}
                          </Badge>
                        ))}
                      </div>
                      <div className="mt-3 text-sm text-muted-foreground">
                        <span>{plan.frequency}</span> • <span>{plan.duration_minutes} dk/oturum</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  Aktif müdahale planı yok
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assessments Tab */}
        <TabsContent value="assessments">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Değerlendirme Geçmişi</CardTitle>
              <Button asChild>
                <Link to={`/students/${studentId}/assessment/new`}>
                  <Plus className="mr-2 h-4 w-4" />
                  Yeni Değerlendirme
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {assessments.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Tür</TableHead>
                      <TableHead>Yüzdelik</TableHead>
                      <TableHead>Risk</TableHead>
                      <TableHead>Değerlendiren</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assessments.map((assessment) => (
                      <TableRow key={assessment.assessment_id}>
                        <TableCell>{assessment.assessment_date}</TableCell>
                        <TableCell>{assessmentTypeNames[assessment.assessment_type]}</TableCell>
                        <TableCell>{assessment.percentile || '-'}%</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={riskColors[assessment.risk_level_result]}>
                            {riskNames[assessment.risk_level_result]}
                          </Badge>
                        </TableCell>
                        <TableCell>{assessment.assessor_name || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  Henüz değerlendirme yapılmamış
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Interventions Tab */}
        <TabsContent value="interventions" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Müdahale Planları</CardTitle>
              <Button asChild>
                <Link to={`/students/${studentId}/intervention/new`}>
                  <Plus className="mr-2 h-4 w-4" />
                  Yeni Plan
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {interventions.length > 0 ? (
                <div className="space-y-4">
                  {interventions.map((plan) => (
                    <div key={plan.plan_id} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-lg">{plan.title}</h4>
                          <p className="text-muted-foreground">{plan.description}</p>
                        </div>
                        <Badge variant="outline" className={
                          plan.status === 'active' ? 'bg-green-100 text-green-700' :
                          plan.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {interventionStatusNames[plan.status]}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Hedef Alanlar:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {plan.target_areas?.map((area, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {area}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Sıklık:</span>
                          <p className="font-medium">{plan.frequency}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Süre:</span>
                          <p className="font-medium">{plan.duration_minutes} dk/oturum</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Başlangıç:</span>
                          <p className="font-medium">{plan.start_date}</p>
                        </div>
                      </div>

                      {plan.goals && plan.goals.length > 0 && (
                        <div className="mt-3">
                          <span className="text-sm text-muted-foreground">Hedefler:</span>
                          <ul className="list-disc list-inside text-sm mt-1">
                            {plan.goals.map((goal, idx) => (
                              <li key={idx}>{goal}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  Müdahale planı bulunmuyor
                </p>
              )}
            </CardContent>
          </Card>

          {/* Sessions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Son Oturumlar</CardTitle>
            </CardHeader>
            <CardContent>
              {sessions.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Süre</TableHead>
                      <TableHead>Öğrenci Tepkisi</TableHead>
                      <TableHead>Gözlemler</TableHead>
                      <TableHead>Yürüten</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((session) => (
                      <TableRow key={session.session_id}>
                        <TableCell>{session.session_date}</TableCell>
                        <TableCell>{session.duration_minutes} dk</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {session.student_response || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {session.progress_observed || '-'}
                        </TableCell>
                        <TableCell>{session.conductor_name || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  Henüz oturum kaydı yok
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Öğrenci Notları</CardTitle>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Not Ekle
              </Button>
            </CardHeader>
            <CardContent>
              {notes && notes.length > 0 ? (
                <div className="space-y-4">
                  {notes.map((note) => (
                    <div key={note.note_id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="secondary">{note.note_type}</Badge>
                        <span className="text-sm text-muted-foreground">
                          {new Date(note.created_at).toLocaleDateString('tr-TR')}
                        </span>
                      </div>
                      <p className="text-sm">{note.content}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {note.creator_name || 'Anonim'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  Henüz not eklenmemiş
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StudentDetailPage;
